import { Fragment, type Node as ProseMirrorNode, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { Extension } from "@tiptap/react";
import { applyCanonicalTopLevelFieldAttrs } from "../schemas/schema-metadata";
import type { DocumentSchemaConfig } from "../schemas/types";
import { firstSectionHeaderIsSelected } from "./first-section-header-selection";

/**
 * Node types that form the document schema structure. Inline atoms such as mentions
 * and documentEvidence are content, so they intentionally remain outside this set.
 */
const STRUCTURAL_TYPES = new Set([
  "sectionTitle",
  "paragraphSection",
  "richSection",
  "atomicField",
]);
const SECTION_HEADER_LABEL_SELECTOR = "[data-df-section-header-label]";
const GENERATED_SECTION_HEADER_SELECTOR = "[data-df-section-header='true']";

/**
 * Structured paste handler for document schema editors.
 *
 * Intercepts paste events containing structural section wrappers and unwraps
 * them into cursor-local content. Structural wrappers are clipboard packaging,
 * not routing instructions: source fieldId must never decide the paste target.
 *
 * Regular text paste falls through to ProseMirror's default paste behavior,
 * except for single-textblock paragraph slices pasted into inline selections.
 * That path is handled here so cursor placement remains inside the target section.
 */
export interface StructuredPasteOptions {
  schemaConfig: DocumentSchemaConfig | null;
}

export const StructuredPaste = Extension.create<StructuredPasteOptions>({
  name: "structuredPaste",

  addOptions() {
    return {
      schemaConfig: null,
    };
  },

  addProseMirrorPlugins() {
    const schemaConfig = this.options.schemaConfig;

    return [
      new Plugin({
        key: new PluginKey("structuredPaste"),
        props: {
          transformCopied(slice) {
            return normalizeCopiedStructuralContext(slice);
          },
          clipboardTextSerializer(slice) {
            return serializeStructuralSliceForClipboard(slice);
          },
          handlePaste(view, event, slice) {
            return handleStructuredPaste(view, event, slice, schemaConfig);
          },
          handleDOMEvents: {
            copy(view, event) {
              return handleHeaderAwareCopy(view, event);
            },
          },
        },
      }),
    ];
  },
});

/**
 * Handle schema-local paste normalization by inserting compatible content at the target selection.
 * Returns true if the paste was handled, false to defer to default.
 */
function handleStructuredPaste(
  view: EditorView,
  event: ClipboardEvent,
  slice: Slice,
  schemaConfig: DocumentSchemaConfig | null
): boolean {
  const generatedHeaderPaste = stripGeneratedSectionHeaderPaste(slice, event);
  const pasteSlice = generatedHeaderPaste.slice;

  if (generatedHeaderPaste.stripped && pasteSlice.content.size === 0) {
    event.preventDefault();
    return true;
  }

  const inlineContent = inlinePasteTargetHasSingleTextblockPayload(view, pasteSlice);
  const hasStructuralNode = hasTopLevelStructuralNode(pasteSlice);
  if (!inlineContent && !hasStructuralNode) {
    if (!generatedHeaderPaste.stripped) return false;
    return replaceSelectionWithContent(view, pasteSlice.content, schemaConfig);
  }

  const normalizedContent =
    inlineContent ?? normalizeStructuralSlice(view, pasteSlice, findTargetStructuralType(view));
  if (normalizedContent.size === 0) return false;

  return replaceSelectionWithContent(view, normalizedContent, schemaConfig);
}

function replaceSelectionWithContent(
  view: EditorView,
  content: Fragment,
  schemaConfig: DocumentSchemaConfig | null
): boolean {
  try {
    const baseTransaction = view.state.tr
      .replaceSelection(new Slice(content, 0, 0))
      .scrollIntoView();

    if (!baseTransaction.docChanged) return false;

    const transaction =
      schemaConfig && baseTransaction.doc.childCount === schemaConfig.fields.length
        ? applyCanonicalTopLevelFieldAttrs(schemaConfig, baseTransaction.doc, baseTransaction).tr
        : baseTransaction;

    view.dispatch(transaction);
    return true;
  } catch {
    return false;
  }
}

interface SelectedSectionHeader {
  fieldId: string;
  label: string;
}

interface HeaderAwareClipboardPayload {
  text: string;
  html: string;
}

function handleHeaderAwareCopy(view: EditorView, event: Event): boolean {
  const copyEvent = event as ClipboardEvent;
  const clipboardData = copyEvent.clipboardData;
  if (!clipboardData) return false;

  const syntheticFirstHeaderSelected = firstSectionHeaderIsSelected(view.state);
  const selectedHeaders = findSelectedSectionHeaders(view, syntheticFirstHeaderSelected);
  if (selectedHeaders.length === 0) return false;

  const payload = buildHeaderAwareClipboardPayload(view, selectedHeaders, {
    forceEditorContent: syntheticFirstHeaderSelected && !view.state.selection.empty,
  });
  if (!payload) return false;

  clipboardData.clearData();
  clipboardData.setData("text/plain", payload.text);
  clipboardData.setData("text/html", payload.html);
  copyEvent.preventDefault();
  return true;
}

function findSelectedSectionHeaders(
  view: EditorView,
  includeSyntheticFirstHeader = false
): SelectedSectionHeader[] {
  const selection = view.dom.ownerDocument.getSelection();
  const elements =
    !selection || selection.isCollapsed || selection.rangeCount === 0
      ? []
      : Array.from(view.dom.querySelectorAll<HTMLElement>(SECTION_HEADER_LABEL_SELECTOR)).filter(
          (element) => sectionHeaderLabelIsSelected(selection, element)
        );

  if (includeSyntheticFirstHeader) {
    const firstHeader = firstDocumentHeader(view);
    if (firstHeader) {
      elements.push(firstHeader);
    }
  }

  elements.sort((a, b) => {
    const position = a.compareDocumentPosition(b);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });

  const selectedHeaders: SelectedSectionHeader[] = [];
  const seenFieldIds = new Set<string>();

  for (const element of elements) {
    const fieldId = element.dataset.fieldId?.trim() ?? "";
    const label = (element.dataset.label ?? element.textContent ?? "").trim();
    if (!fieldId || !label || seenFieldIds.has(fieldId)) continue;

    selectedHeaders.push({ fieldId, label });
    seenFieldIds.add(fieldId);
  }

  return selectedHeaders;
}

function firstDocumentHeader(view: EditorView): HTMLElement | null {
  const firstNode = view.state.doc.firstChild;
  const fieldId = typeof firstNode?.attrs.fieldId === "string" ? firstNode.attrs.fieldId : "";
  if (!fieldId) return null;

  return (
    Array.from(view.dom.querySelectorAll<HTMLElement>(SECTION_HEADER_LABEL_SELECTOR)).find(
      (element) => element.dataset.fieldId === fieldId
    ) ?? null
  );
}

function sectionHeaderLabelIsSelected(selection: Selection, element: HTMLElement): boolean {
  return selectionIntersectsNode(selection, element);
}

function selectionContainsEditableEditorContent(view: EditorView, selection: Selection): boolean {
  const walker = view.dom.ownerDocument.createTreeWalker(view.dom, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();

  while (currentNode) {
    const parent = currentNode.parentElement;
    const text = currentNode.textContent ?? "";
    if (
      parent &&
      text.trim().length > 0 &&
      !parent.closest(SECTION_HEADER_LABEL_SELECTOR) &&
      !parent.closest(".document-field-chrome") &&
      selectionIntersectsNode(selection, currentNode)
    ) {
      return true;
    }

    currentNode = walker.nextNode();
  }

  return false;
}

function selectionIntersectsNode(selection: Selection, node: Node): boolean {
  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);
    if (range.collapsed) continue;

    try {
      if (range.intersectsNode(node)) {
        return true;
      }
    } catch {
      // Ignore detached or browser-incompatible range nodes.
    }
  }

  return false;
}

function buildHeaderAwareClipboardPayload(
  view: EditorView,
  selectedHeaders: SelectedSectionHeader[],
  options: { forceEditorContent?: boolean } = {}
): HeaderAwareClipboardPayload | null {
  const selectedHeaderByFieldId = new Map(
    selectedHeaders.map((header) => [header.fieldId, header])
  );
  const emittedHeaderFieldIds = new Set<string>();
  const textParts: string[] = [];
  const htmlParts: string[] = [];
  const { selection, doc } = view.state;
  const domSelection = view.dom.ownerDocument.getSelection();
  const includeEditorContent =
    options.forceEditorContent === true ||
    (domSelection !== null && selectionContainsEditableEditorContent(view, domSelection));

  doc.forEach((node, offset) => {
    const fieldId = typeof node.attrs.fieldId === "string" ? node.attrs.fieldId : "";
    const selectedHeader = selectedHeaderByFieldId.get(fieldId);

    if (selectedHeader) {
      appendClipboardPart(textParts, htmlParts, {
        text: selectedHeader.label,
        html: renderGeneratedSectionHeaderHtml(selectedHeader.label),
      });
      emittedHeaderFieldIds.add(fieldId);
    }

    if (selection.empty || !includeEditorContent) return;

    const contentStart = offset + 1;
    const contentEnd = offset + node.nodeSize - 1;
    const selectedFrom = Math.max(selection.from, contentStart);
    const selectedTo = Math.min(selection.to, contentEnd);
    if (selectedFrom >= selectedTo) return;

    const serialized = view.serializeForClipboard(doc.slice(selectedFrom, selectedTo));
    appendClipboardPart(textParts, htmlParts, {
      text: serialized.text,
      html: serialized.dom.innerHTML,
    });
  });

  for (const header of selectedHeaders) {
    if (emittedHeaderFieldIds.has(header.fieldId)) continue;

    appendClipboardPart(textParts, htmlParts, {
      text: header.label,
      html: renderGeneratedSectionHeaderHtml(header.label),
    });
  }

  if (textParts.length === 0 && htmlParts.length === 0) {
    return null;
  }

  return {
    text: textParts.join("\n"),
    html: `<meta charset="utf-8">${htmlParts.join("")}`,
  };
}

function appendClipboardPart(
  textParts: string[],
  htmlParts: string[],
  part: HeaderAwareClipboardPayload
): void {
  if (part.text.length > 0) {
    textParts.push(part.text);
  }
  if (part.html.length > 0) {
    htmlParts.push(part.html);
  }
}

function renderGeneratedSectionHeaderHtml(label: string): string {
  return `<h2 data-df-section-header="true"><strong>${escapeHtml(label)}</strong></h2>`;
}

function stripGeneratedSectionHeaderPaste(
  slice: Slice,
  event: ClipboardEvent
): { slice: Slice; stripped: boolean } {
  const labels = generatedSectionHeaderLabelsFromHtml(
    event.clipboardData?.getData("text/html") ?? ""
  );
  if (labels.length === 0) {
    return { slice, stripped: false };
  }

  const remainingLabels = new Map<string, number>();
  for (const label of labels) {
    remainingLabels.set(label, (remainingLabels.get(label) ?? 0) + 1);
  }

  const nodes: ProseMirrorNode[] = [];
  let stripped = false;

  slice.content.forEach((node) => {
    if (consumeGeneratedSectionHeaderNode(node, remainingLabels)) {
      stripped = true;
      return;
    }

    nodes.push(node);
  });

  return stripped
    ? { slice: new Slice(Fragment.fromArray(nodes), 0, 0), stripped }
    : { slice, stripped };
}

function generatedSectionHeaderLabelsFromHtml(html: string): string[] {
  if (!html.includes("data-df-section-header")) {
    return [];
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  return Array.from(container.querySelectorAll<HTMLElement>(GENERATED_SECTION_HEADER_SELECTOR))
    .map((element) => element.textContent?.trim() ?? "")
    .filter((label) => label.length > 0);
}

function consumeGeneratedSectionHeaderNode(
  node: ProseMirrorNode,
  remainingLabels: Map<string, number>
): boolean {
  if (node.type.name !== "heading" || node.attrs.level !== 2) {
    return false;
  }

  const label = node.textContent.trim();
  const remaining = remainingLabels.get(label) ?? 0;
  if (remaining <= 0) {
    return false;
  }

  if (remaining === 1) {
    remainingLabels.delete(label);
  } else {
    remainingLabels.set(label, remaining - 1);
  }
  return true;
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function normalizeCopiedStructuralContext(slice: Slice): Slice {
  if (slice.openStart === 0 && slice.openEnd === 0) {
    return slice;
  }

  const normalizedNodes: ProseMirrorNode[] = [];
  let changed = false;

  slice.content.forEach((node) => {
    if (STRUCTURAL_TYPES.has(node.type.name)) {
      changed = true;
      normalizedNodes.push(...visibleClipboardChildrenFromStructuralNode(node));
      return;
    }

    if (isEmptyClipboardContextNode(node)) {
      changed = true;
      return;
    }

    normalizedNodes.push(node);
  });

  if (!changed) {
    return slice;
  }

  return new Slice(Fragment.fromArray(normalizedNodes), 0, 0);
}

function visibleClipboardChildrenFromStructuralNode(node: ProseMirrorNode): ProseMirrorNode[] {
  if (node.type.name === "atomicField") {
    return [];
  }

  if (node.type.name === "sectionTitle") {
    const paragraphType = node.type.schema.nodes.paragraph;
    if (!paragraphType || node.textContent.trim().length === 0) {
      return [];
    }
    return [paragraphType.create(null, node.content)];
  }

  const nodes: ProseMirrorNode[] = [];
  node.content.forEach((child) => {
    nodes.push(child);
  });
  return nodes.every(isEmptyClipboardContextNode) ? [] : nodes;
}

function isEmptyClipboardContextNode(node: ProseMirrorNode): boolean {
  return node.isTextblock && node.content.size === 0;
}

function inlinePasteTargetHasSingleTextblockPayload(
  view: EditorView,
  slice: Slice
): Fragment | null {
  const { $from, $to } = view.state.selection;
  if (!$from.parent.inlineContent || $from.parent !== $to.parent) {
    return null;
  }

  return singleInlinePayloadFromSlice(slice);
}

function hasTopLevelStructuralNode(slice: Slice): boolean {
  let hasStructuralNode = false;
  slice.content.forEach((node) => {
    if (STRUCTURAL_TYPES.has(node.type.name)) {
      hasStructuralNode = true;
    }
  });
  return hasStructuralNode;
}

function normalizeStructuralSlice(
  view: EditorView,
  slice: Slice,
  targetType: string | null
): Fragment {
  const nodes: ProseMirrorNode[] = [];
  const paragraphType = view.state.schema.nodes.paragraph;

  slice.content.forEach((node) => {
    switch (node.type.name) {
      case "richSection":
      case "paragraphSection":
        node.content.forEach((child) => {
          const normalizedChild = normalizeNodeForTarget(view, child, targetType);
          if (normalizedChild) {
            nodes.push(normalizedChild);
          }
        });
        break;
      case "sectionTitle":
        if (paragraphType && node.content.size > 0) {
          const titleParagraph = paragraphType.create(null, node.content);
          const normalizedTitle = normalizeNodeForTarget(view, titleParagraph, targetType);
          if (normalizedTitle) {
            nodes.push(normalizedTitle);
          }
        }
        break;
      case "atomicField":
        // Atomic fields are app-owned section widgets, not text paste payloads.
        break;
      default:
        nodes.push(node);
    }
  });

  return Fragment.fromArray(nodes);
}

function singleInlinePayloadFromSlice(slice: Slice): Fragment | null {
  let inlinePayload: Fragment | null = null;
  let payloadCount = 0;
  let unsupportedPayload = false;

  slice.content.forEach((node) => {
    const nodePayload = singleInlinePayloadFromNode(node);
    if (!nodePayload || nodePayload.size === 0) {
      unsupportedPayload = true;
      return;
    }

    inlinePayload = nodePayload;
    payloadCount += 1;
  });

  return !unsupportedPayload && payloadCount === 1 ? inlinePayload : null;
}

function singleInlinePayloadFromNode(node: ProseMirrorNode): Fragment | null {
  switch (node.type.name) {
    case "richSection":
    case "paragraphSection":
      if (node.childCount !== 1) return null;
      return inlinePayloadFromTextblock(node.child(0));
    case "sectionTitle":
      return node.content;
    case "atomicField":
      return null;
    default:
      return inlinePayloadFromTextblock(node);
  }
}

function inlinePayloadFromTextblock(node: ProseMirrorNode): Fragment | null {
  return node.isTextblock ? node.content : null;
}

function findTargetStructuralType(view: EditorView): string | null {
  const { $from } = view.state.selection;
  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (STRUCTURAL_TYPES.has(node.type.name)) {
      return node.type.name;
    }
  }
  return null;
}

function normalizeNodeForTarget(
  view: EditorView,
  node: ProseMirrorNode,
  targetType: string | null
): ProseMirrorNode | null {
  if (targetType === "paragraphSection" && node.type.name !== "paragraph") {
    return createPlainParagraph(view, node.textContent);
  }

  if (targetType === "sectionTitle") {
    return createPlainText(view, node.textContent);
  }

  return node;
}

function createPlainText(view: EditorView, text: string): ProseMirrorNode | null {
  const trimmedText = text.trim();
  return trimmedText ? view.state.schema.text(trimmedText) : null;
}

function createPlainParagraph(view: EditorView, text: string): ProseMirrorNode | null {
  const paragraphType = view.state.schema.nodes.paragraph;
  if (!paragraphType) return null;

  const trimmedText = text.trim();
  return paragraphType.create(null, trimmedText ? view.state.schema.text(trimmedText) : undefined);
}

function serializeStructuralSliceForClipboard(slice: Slice): string {
  const parts: string[] = [];

  slice.content.forEach((node) => {
    parts.push(serializeNodeForClipboard(node));
  });

  return parts.filter((part) => part.length > 0).join("\n\n");
}

function serializeNodeForClipboard(node: ProseMirrorNode): string {
  if (!STRUCTURAL_TYPES.has(node.type.name)) {
    return node.textBetween(0, node.content.size, "\n");
  }

  const label = typeof node.attrs.label === "string" ? node.attrs.label.trim() : "";
  const text =
    node.type.name === "atomicField"
      ? typeof node.attrs.value === "string"
        ? node.attrs.value.trim()
        : ""
      : node.textBetween(0, node.content.size, "\n").trim();

  if (label && text) return `${label}\n${text}`;
  return label || text;
}
